// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/widget-installation.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / actions / custom-tabs).

import type { DetailPageConfig } from "../../components/composition/DetailPage";
import { widgetInstallationsApi } from "../../api/widget-installations";
import type { WidgetInstallation } from "../../api/types";
// override-block:imports BEGIN
// CHG-2463 / ITER-905205 — Widget Studio S4.
import { createElement } from "react";
import type { ReactNode } from "react";
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
import { openInstallationStatusOverride } from "../../lib/widget-installation-actions";
// override-block:imports END

// override-block:helpers BEGIN
// Installation observational-status badge (widget-installation-status
// value-space). Design-system tone tokens only (composition-lint).
const INSTALLATION_TONE: Record<string, { bg: string; fg: string }> = {
  active:   { bg: "var(--color-success-tint)", fg: "var(--color-success)" },
  observed: { bg: "var(--color-info-tint)",    fg: "var(--color-info)" },
  stale:    { bg: "var(--color-warning-tint)", fg: "var(--color-warning)" },
  removed:  { bg: "var(--color-bg-subtle)",    fg: "var(--color-text-secondary)" },
  blocked:  { bg: "var(--color-error-tint)",   fg: "var(--color-error)" },
  error:    { bg: "var(--color-error-tint)",   fg: "var(--color-error)" },
};

function installationStatusBadge(value: string | null | undefined): ReactNode {
  const status = (value ?? "").toString();
  const tone = INSTALLATION_TONE[status] ?? {
    bg: "var(--color-bg-subtle)",
    fg: "var(--color-text-secondary)",
  };
  return createElement(
    "span",
    {
      style: {
        display: "inline-flex",
        alignItems: "center",
        height: 22,
        padding: "0 10px",
        fontSize: 12,
        fontWeight: 600,
        background: tone.bg,
        color: tone.fg,
        borderRadius: 9999,
        textTransform: "capitalize",
      },
    },
    status ? status.replace(/_/g, " ") : "—",
  );
}

// Endpoint-citing placeholder card for the page-observations tab (full table
// lands in S5). Mirrors the instance-detail placeholder pattern.
function tabCard(body: string): ReactNode {
  return createElement(
    "div",
    {
      className: "card",
      style: { padding: 16, color: "var(--color-text-muted)", fontSize: 13, lineHeight: 1.6 },
    },
    body,
  );
}
// override-block:helpers END

export const widgetInstallationDetailConfig: DetailPageConfig<WidgetInstallation> = {
  idParam: "widgetInstallationId",
  queryKey: (id: string) => ["widget-installations", "detail", id] as const,
  queryFn: (id: string) => widgetInstallationsApi.getWidgetInstallation(id),
  backTo: "/channels/widgets/installations",
  // override-block:title BEGIN
  title: (r) => r.domain,
  // override-block:title END
  // override-block:subtitle BEGIN
  subtitle: (r) => r.url_pattern ?? null,
  // override-block:subtitle END
  statusCards: (r, _fmt) => [
    {
      id: "impressions",
      label: "Impressions",
      value: r.impression_count,
      format: "number",
      tone: "g-events",
    },
    {
      id: "viewable",
      label: "Viewable",
      value: r.viewable_count,
      format: "number",
      tone: "g-events",
    },
    {
      id: "clicks",
      label: "Clicks",
      value: r.click_count,
      format: "number",
      tone: "g-mygiving",
    },
    // override-block:statcard-status BEGIN
    {
      id: "status",
      label: "Status",
      value: null,
      format: "text",
      tone: "g-people",
      render: installationStatusBadge(r.status),
    },
    // override-block:statcard-status END
  ],
  actions: (_r) => [
    // override-block:actions BEGIN
    // The only write is the inline observational-status override (PATCH).
    // RBAC-gated (GATE_CATALOG.widget_installation_detail); the BE re-enforces
    // widget-platform.configure and 400s an invalid status. view-instance is a
    // read-level navigation to the owning instance detail.
    {
      id: "override-status",
      label: "Override status",
      variant: "secondary",
      permissionGate: toGate(GATE_CATALOG.widget_installation_detail["override-status"], "disable"),
      onClick: () => openInstallationStatusOverride(_r),
    },
    {
      id: "view-instance",
      label: "View widget",
      variant: "ghost",
      href: `/channels/widgets/${_r.widget_instance_id}`,
      permissionGate: toGate(GATE_CATALOG.widget_installation_detail["view-instance"], "hide"),
    },
    // override-block:actions END
  ],
  tabs: [
    // override-block:tab-overview BEGIN
    {
      id: "overview",
      label: "Overview",
      render: (r, fmt) => {
        const rows: Array<[string, string]> = [
          ["Domain", r.domain ?? "—"],
          ["Origin", r.origin ?? "—"],
          ["URL pattern", r.url_pattern ?? "—"],
          ["Mount kind", (r.mount_kind ?? "—").toString().replace(/_/g, " ")],
          ["Surface", r.surface ?? "—"],
          ["Renderer mode", (r.renderer_mode ?? "—").toString().replace(/_/g, " ")],
          ["Error count", String(r.error_count ?? 0)],
          ["First seen", r.first_seen_at ? fmt.formatDate(r.first_seen_at, "long") : "—"],
          ["Last seen", r.last_seen_at ? fmt.formatDate(r.last_seen_at, "relative") : "—"],
          ["Last rendered", r.last_rendered_at ? fmt.formatDate(r.last_rendered_at, "relative") : "—"],
        ];
        return createElement(
          "div",
          { className: "card", style: { padding: 16, fontSize: 13, lineHeight: 1.8 } },
          ...rows.map(([k, v]) =>
            createElement(
              "div",
              { key: k, style: { display: "flex", justifyContent: "space-between", gap: 16 } },
              createElement("span", { style: { color: "var(--color-text-muted)" } }, k),
              createElement("span", { style: { textTransform: "capitalize" } }, v),
            ),
          ),
        );
      },
    },
    // override-block:tab-overview END
    // override-block:tab-observations BEGIN
    {
      id: "observations",
      label: "Page observations",
      render: (r) =>
        tabCard(
          `Per-page telemetry for this installation's widget — GET /admin/widgets/page-observations?widget_instance_id=${r.widget_instance_id}. Full table lands in S5.`,
        ),
    },
    // override-block:tab-observations END
    // override-block:custom-tabs BEGIN
    // override-block:custom-tabs END
  ],
};
